<template>
  <div class="customers">
    <RouterView />
    <div class="box customers-list wrapper-customers">
      <div class="title-container is-flex">
        <h2 class="title is-3 has-text-primary title-customers">
          Mis clientes
        </h2>
        <div class="buttons">
          <div class="field is-grouped">
            <p class="control">
              <Button
                class="button is-primary btn-add-customer"
                @click="openModal"
              >
                <BIcon
                  icon="plus"
                />
                <span>
                  Nuevo cliente
                </span>
              </Button>
            </p>
            <p class="control">
              <RouterLink
                class="button is-illuminating-emerald btn-bulk-upload"
                :to="{ name: 'CustomerBulkUpload' }"
              >
                <BIcon
                  icon="cloud-upload-outline"
                />
                <span>
                  Carga masiva
                </span>
              </RouterLink>
            </p>
          </div>
        </div>
      </div>

      <div class="table-container">
        <BTable
          :data="$store.state.customers.list"
          :loading="$store.state.customers.loading"
          :total="$store.state.customers.count"
          :current-page="page"
          :columns="columns"
          :per-page="size"
          backend-pagination
          paginated
          @page-change="changePage"
        >
          <template slot-scope="props">
            <BTableColumn
              field="companyName"
              label="Nombre de la empresa"
              internal
            >
              <RouterLink
                class="action-option"
                :to="{ name: 'CustomerEdit', params: { customer: props.row.id } }"
              >
                {{ props.row.companyName }}
              </RouterLink>
            </BTableColumn>

            <BTableColumn
              field="rfc"
              label="RFC"
              internal
            >
              <RouterLink
                class="action-option"
                :to="{ name: 'CustomerEdit', params: { customer: props.row.id } }"
              >
                {{ props.row.rfc }}
              </RouterLink>
            </BTableColumn>

            <BTableColumn
              field="email"
              label="Contacto"
              internal
            >
              {{ props.row.email }}
            </BTableColumn>

            <BTableColumn
              field="totalDebt"
              label="Deuda total"
              internal
            >
              <div
                v-for="(debt, index) in props.row.debtsCustomer"
                :key="index"
              >
                {{ debt.totalAmount | toCurrency(debt._id.currency) }}
              </div>
            </BTableColumn>

            <BTableColumn
              field="totalDebtExpired"
              label="Deuda vencida"
              internal
            >
              <div
                v-for="(debt, index) in props.row.debtsExpired"
                :key="index"
                class="has-text-danger"
              >
                {{ debt.totalAmount | toCurrency(debt._id.currency) }}
              </div>
            </BTableColumn>

            <BTableColumn
              field="availableCredit"
              label="Crédito disponible"
              internal
            >
              <template v-if="props.row.creditLimit > 0">
                <div
                  v-for="(debt, index) in props.row.debtCreditLimit"
                  :key="index"
                >
                  {{ props.row.creditLimit-debt.totalAmount | toCurrency(debt.currency) }}
                </div>
              </template>
            </BTableColumn>

            <BTableColumn
              field="controls"
              label=""
              internal
            >
              <div class="is-flex is-centered">
                <BTooltip
                  :label="props.row.accStatusToken ? 'Con Edo de Cuenta' : 'Sin Edo de Cuenta'"
                  position="is-left"
                >
                  <BIcon
                    v-if="props.row.accStatusToken"
                    icon="file-check"
                    type="is-success"
                  />
                  <BIcon
                    v-if="!props.row.accStatusToken"
                    icon="file-cancel"
                    type="is-danger"
                  />
                </BTooltip>

                <AppRadialMenu
                  :size="36"
                  :item-size="48"
                  :radius="80"
                  :angle-restriction="140"
                  :rotate="-80"
                >
                  <AppRadialMenuItem
                    :class="deleteClass"
                    data-tooltip="Eliminar"
                  >
                    <RouterLink
                      class="action-option"
                      :to="{ name: 'CustomerDelete', params: { customer: props.row.id } }"
                    >
                      <BIcon
                        icon="delete"
                        class="action-option-icon"
                      />
                    </RouterLink>
                  </AppRadialMenuItem>

                  <AppRadialMenuItem
                    :class="editClass"
                    data-tooltip="Editar"
                  >
                    <RouterLink
                      class="action-option"
                      :to="{ name: 'CustomerEdit', params: { customer: props.row.id } }"
                    >
                      <BIcon
                        icon="pencil"
                        class="action-option-icon"
                      />
                    </RouterLink>
                  </AppRadialMenuItem>

                  <AppRadialMenuItem
                    class="tooltip is-tooltip-danger has-background-danger has-text-white"
                    data-tooltip="Remover estado de cuenta"
                  >
                    <RouterLink
                      class="action-option"
                      :to="{
                        name: 'StatusAccountDelete',
                        params: {
                          customer: props.row.id,
                          company: props.row.companyId
                        }
                      }"
                    >
                      <BIcon
                        icon="cancel"
                        class="action-option-icon"
                      />
                    </RouterLink>
                  </AppRadialMenuItem>

                  <AppRadialMenuItem
                    class="tooltip is-tooltip-success has-background-success has-text-white"
                    data-tooltip="Enviar estado de cuenta"
                  >
                    <a
                      class="action-option"
                      @click="sendStatusAccountEmail(props.row.id, props.row.companyId)"
                    >
                      <BIcon
                        icon="send"
                        class="action-option-icon"
                      />
                    </a>
                  </AppRadialMenuItem>

                  <AppRadialMenuItem
                    :class="commentClass"
                    data-tooltip="Conversar"
                  >
                    <RouterLink
                      class="action-option"
                      :to="{
                        name: 'CustomerChat',
                        params: {
                          customer: props.row.id,
                          company: props.row.companyId
                        }
                      }"
                    >
                      <BIcon
                        icon="comment"
                        class="action-option-icon"
                      />
                    </RouterLink>
                  </AppRadialMenuItem>
                </AppRadialMenu>
              </div>
            </BTableColumn>
          </template>
        </BTable>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Customers',

  components: {
    AppRadialMenu: () => import(/* webpackChunkName: "app-radial-menu" */ '@/components/AppRadialMenu.vue'),
    AppRadialMenuItem: () => import(/* webpackChunkName: "app-radial-menu-item" */ '@/components/AppRadialMenuItem.vue'),
  },

  data() {
    return {
      deleteClass: 'tooltip is-tooltip-info has-background-info has-text-white is-tooltip-left',
      editClass: 'tooltip is-tooltip-link has-background-link has-text-white is-tooltip-left',
      commentClass: 'tooltip  has-background-grey has-text-white is-tooltip-left',
      columns: [
        { field: 'companyName', label: 'Nombre de la empresa' },
        { field: 'rfc', label: 'RFC' },
        { field: 'email', label: 'Contacto' },
        { field: 'totalDebt', label: 'Deuda total' },
        { field: 'totalDebtExpired', label: 'Deuda vencida' },
        { field: 'availableCredit', label: 'Crédito disponible' },
        { field: 'controls', label: 'Acciones' },
      ],
    };
  },

  computed: {
    page() {
      return parseInt(this.$route.query.page, 10);
    },
    size() {
      return parseInt(this.$route.query.size, 10);
    },
  },

  beforeRouteEnter({ name, query }, from, next) {
    if (name === 'Customers') {
      if (query.page && query.size) {
        next();
      } else {
        const { page = 1, size = 20 } = query;
        next({ name, query: { page, size } });
      }
    } else {
      next();
    }
  },

  async beforeRouteUpdate({ name, query }, from, next) {
    this.$store.commit('customers/setValue', { accessor: 'email', value: '' });
    if (name === 'Customers') {
      if (query.page && query.size) {
        const { page = 1, size = 20 } = query;
        await this.$store.dispatch('customers/getAll', { page, size, withDebts: true });
        next();
      } else {
        const { page = 1, size = 20 } = query;
        this.$store.dispatch('customers/getAll', { page, size, withDebts: true });
        next({ name, query: { page, size } });
      }
    } else {
      next();
    }
  },

  async mounted() {
    const { page = 1, size = 20 } = this.$route.query;

    this.$store.commit('customers/setValue', { accessor: 'email', value: '' });

    await Promise.all([
      this.$store.dispatch('customers/getAll', { page, size, withDebts: true }),
    ]);
  },

  methods: {
    changePage(event) {
      this.$router.push({
        name: 'Customers',
        query: { page: event, size: 20 },
      });
    },

    openModal() {
      this.$modalForm({
        show: true,
        component: 'NewCustomer',
        title: 'Nuevo cliente',
        btnSave: 'Crear Cliente',
      });
    },

    async sendStatusAccountEmail(customerId, companyId) {
      const now = new Date();

      this.$store.commit('ui/addNotification', {
        type: 'info',
        content: 'Enviando Estado de Cuenta...',
        dateTime: now,
      });

      await this.$store.dispatch(
        'customers/sendStatusAccountEmail',
        { customerId, companyId },
      );

      this.$store.commit('ui/deleteNotification', now);

      this.$store.dispatch('addTemporalNotification', {
        type: 'success',
        content: 'El estado de cuenta se envió correctamente.',
      });

      const { page, size } = this.$route.query;

      await this.$store.dispatch('customers/getAll', { page, size });
    },
  },
};
</script>

<style scoped>
.customers {
  padding: 1rem;
}

.count{
  color: #4a4a4a;
  font-size: 18px;
}

.box {
  position: relative;
}

.box-count {
  width:calc(100% - 40px);
  border:1px solid black;
  position: absolute;
  bottom: 46px;
  display:flex;
  flex-direction: row-reverse;
  padding-right: 210px;
}

.customers {
  padding: 1rem;
  padding-bottom: 6rem;
}

.title-container.is-flex {
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  flex-wrap: wrap;
}

.title {
  margin-bottom: 0;
}

.customers-list .b-table {
  padding-top: 16px;
}

@media screen and (max-width: 768px) {
  .title {
    margin-bottom: 1rem;
  }
}
</style>

<style lang="scss">
.customers .pagination {
  margin-right: 4px;
}

.customers a.action-option {
  height: 1.5rem;
}
</style>
